<script setup lang="ts">
import { computed, ref } from 'vue'
import PriceChartA11yTable from './PriceChartA11yTable.vue'
import Accordion from './Accordion.vue'
import { formatHour, formatPricePerKwh } from '../lib/format'
import type { HourPrice } from '@greenbow/composables'

interface OptimalSlot {
  hour: number
  dayOffset: 0 | 1
}

const props = defineProps<{
  today: readonly HourPrice[]
  tomorrow?: readonly HourPrice[] | null
  currentHour: number
  optimalSlots?: readonly OptimalSlot[]
  defaultOpen?: boolean
}>()

const emit = defineEmits<{
  toggle: [open: boolean]
}>()

// Logical viewBox dimensions — SVG scales responsively via CSS.
const VIEW_W = 800
const VIEW_H = 280
const PAD_LEFT = 64
const PAD_RIGHT = 16
const PAD_TOP = 36
const PAD_BOTTOM = 36

interface ChartPoint {
  index: number
  hour: number
  price: number
  x: number
  y: number
  isTomorrow: boolean
}

const allPoints = computed<ChartPoint[]>(() => {
  const todayPoints = props.today.map((p, i) => ({
    index: i,
    hour: p.hour,
    price: p.price,
    isTomorrow: false,
    x: 0,
    y: 0,
  }))
  const tomorrowPoints = (props.tomorrow ?? []).map((p, i) => ({
    index: todayPoints.length + i,
    hour: p.hour,
    price: p.price,
    isTomorrow: true,
    x: 0,
    y: 0,
  }))
  return [...todayPoints, ...tomorrowPoints]
})

// Picks a tick step that lands on a "nice" number (0.1, 0.2, 0.25, 0.5, 1, 2…)
// given a desired step count. Standard chart library algorithm.
const NICE_STEPS = [0.1, 0.2, 0.25, 0.5, 1, 2, 2.5, 5, 10, 20, 25]
function niceStep(range: number, targetSteps: number): number {
  const rough = range / targetSteps
  for (const step of NICE_STEPS) {
    if (step >= rough) return step
  }
  return Math.ceil(rough)
}

const Y_STEP_COUNT = 4

const yScale = computed(() => {
  if (allPoints.value.length === 0) return { min: 0, max: 1, step: 0.25 }
  const prices = allPoints.value.map((p) => p.price)
  const rawMin = Math.min(0, ...prices)
  const rawMax = Math.max(...prices) * 1.05
  if (rawMax === rawMin) {
    return { min: rawMin, max: rawMin + 1, step: 0.25 }
  }
  const step = niceStep(rawMax - rawMin, Y_STEP_COUNT)
  const min = Math.floor(rawMin / step) * step
  const max = Math.ceil(rawMax / step) * step
  return { min, max, step }
})

const positionedPoints = computed<ChartPoint[]>(() => {
  const pts = allPoints.value
  if (pts.length === 0) return []
  const innerW = VIEW_W - PAD_LEFT - PAD_RIGHT
  const innerH = VIEW_H - PAD_TOP - PAD_BOTTOM
  const stepX = pts.length > 1 ? innerW / (pts.length - 1) : 0
  const { min, max } = yScale.value
  return pts.map((p) => ({
    ...p,
    x: PAD_LEFT + p.index * stepX,
    y: PAD_TOP + innerH - ((p.price - min) / (max - min)) * innerH,
  }))
})

const linePath = computed(() => {
  const pts = positionedPoints.value
  if (pts.length === 0) return ''
  return pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ')
})

const averagePrice = computed(() => {
  const pts = allPoints.value
  if (pts.length === 0) return null
  return pts.reduce((sum, p) => sum + p.price, 0) / pts.length
})

const averageY = computed(() => {
  const avg = averagePrice.value
  if (avg === null) return null
  const innerH = VIEW_H - PAD_TOP - PAD_BOTTOM
  const { min, max } = yScale.value
  return PAD_TOP + innerH - ((avg - min) / (max - min)) * innerH
})

// Builds filled path strings for each contiguous group of optimal
// hours. Each path follows the price line through the group's points
// and closes down to the x-axis, so the green highlight sits *under*
// the line — matching the existing navy area fill in style.
const optimalBandPaths = computed<string[]>(() => {
  const slots = props.optimalSlots
  if (!slots || slots.length === 0) return []
  const pts = positionedPoints.value
  if (pts.length === 0) return []

  // Resolve slots → point indices (respecting today/tomorrow)
  const indices: number[] = []
  for (const slot of slots) {
    const wantTomorrow = slot.dayOffset === 1
    const idx = pts.findIndex((p) => p.hour === slot.hour && p.isTomorrow === wantTomorrow)
    if (idx >= 0) indices.push(idx)
  }
  indices.sort((a, b) => a - b)
  if (indices.length === 0) return []

  // Group into contiguous runs so "22,23,00,01,02" renders as a single
  // shape with one smooth outline.
  const groups: number[][] = [[indices[0]!]]
  for (let i = 1; i < indices.length; i++) {
    const last = groups[groups.length - 1]!
    if (indices[i]! === last[last.length - 1]! + 1) {
      last.push(indices[i]!)
    } else {
      groups.push([indices[i]!])
    }
  }

  const bottom = VIEW_H - PAD_BOTTOM
  return groups.map((group) => {
    const first = pts[group[0]!]!
    const last = pts[group[group.length - 1]!]!
    const topLine = group.map((i) => `L ${pts[i]!.x.toFixed(1)} ${pts[i]!.y.toFixed(1)}`).join(' ')
    return `M ${first.x.toFixed(1)} ${bottom} ${topLine} L ${last.x.toFixed(1)} ${bottom} Z`
  })
})

const areaPath = computed(() => {
  const pts = positionedPoints.value
  if (pts.length === 0) return ''
  const bottom = VIEW_H - PAD_BOTTOM
  const firstX = pts[0]!.x.toFixed(1)
  const lastX = pts[pts.length - 1]!.x.toFixed(1)
  const line = pts.map((p) => `L ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ')
  return `M ${firstX} ${bottom} ${line} L ${lastX} ${bottom} Z`
})

const cheapestPoint = computed(() => {
  const pts = positionedPoints.value
  if (pts.length === 0) return null
  return pts.reduce((min, p) => (p.price < min.price ? p : min), pts[0]!)
})

const expensivePoint = computed(() => {
  const pts = positionedPoints.value
  if (pts.length === 0) return null
  return pts.reduce((max, p) => (p.price > max.price ? p : max), pts[0]!)
})

const todayDividerX = computed(() => {
  const todayLength = props.today.length
  const total = allPoints.value.length
  if (todayLength === 0 || total <= todayLength) return null
  const innerW = VIEW_W - PAD_LEFT - PAD_RIGHT
  return PAD_LEFT + ((todayLength - 0.5) / (total - 1)) * innerW
})

const currentHourX = computed(() => {
  const pt = positionedPoints.value.find((p) => !p.isTomorrow && p.hour === props.currentHour)
  return pt ? pt.x : null
})

const yTicks = computed(() => {
  const { min, max, step } = yScale.value
  const innerH = VIEW_H - PAD_TOP - PAD_BOTTOM
  const ticks: { value: number; y: number; label: string }[] = []
  // Guard against floating-point drift at the upper bound
  const epsilon = step / 1000
  for (let value = min; value <= max + epsilon; value += step) {
    const y = PAD_TOP + innerH - ((value - min) / (max - min)) * innerH
    ticks.push({ value, y, label: formatYTick(value, step) })
  }
  return ticks
})

// Smart tick formatter — drops decimals when step is a whole number,
// keeps one decimal for 0.1–0.9 steps, two for sub-0.1 steps.
function formatYTick(value: number, step: number): string {
  const digits = step >= 1 ? 0 : step >= 0.1 ? 1 : 2
  const formatter = new Intl.NumberFormat('da-DK', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
  return `${formatter.format(value)} kr`
}

const xTicks = computed(() => {
  const pts = positionedPoints.value
  if (pts.length === 0) return []
  return pts.filter((p) => p.hour % 4 === 0)
})

const tooltipIndex = ref<number | null>(null)
const tooltipPoint = computed(() => {
  if (tooltipIndex.value === null) return null
  return positionedPoints.value[tooltipIndex.value] ?? null
})

// Animation restarts each time the accordion opens so users get the
// draw-in effect on every expand, not just the first one.
const animationKey = ref(0)

function onToggle(open: boolean) {
  if (open) animationKey.value += 1
  emit('toggle', open)
}

function onPointerMove(event: PointerEvent) {
  const svg = event.currentTarget as SVGElement
  const rect = svg.getBoundingClientRect()
  const xRatio = (event.clientX - rect.left) / rect.width
  const xLogical = xRatio * VIEW_W

  const pts = positionedPoints.value
  if (pts.length === 0) return

  let nearest = 0
  let bestDist = Infinity
  for (let i = 0; i < pts.length; i++) {
    const dist = Math.abs(pts[i]!.x - xLogical)
    if (dist < bestDist) {
      bestDist = dist
      nearest = i
    }
  }
  tooltipIndex.value = nearest
}

function onPointerLeave() {
  tooltipIndex.value = null
}
</script>

<template>
  <Accordion class="price-chart" :default-open="defaultOpen" @toggle="onToggle">
    <template #summary>
      <span class="price-chart__caption">Spotpriser pr. time</span>
    </template>
    <div class="price-chart__body">
      <ul class="price-chart__legend" aria-label="Graf-forklaring">
        <li v-if="optimalBandPaths.length > 0" class="price-chart__legend-item">
          <span class="price-chart__legend-band" aria-hidden="true" />
          Valgte ladetimer
        </li>
        <li class="price-chart__legend-item">
          <span
            class="price-chart__legend-dot price-chart__legend-dot--cheapest"
            aria-hidden="true"
          />
          Billigst
        </li>
        <li class="price-chart__legend-item">
          <span
            class="price-chart__legend-dot price-chart__legend-dot--expensive"
            aria-hidden="true"
          />
          Dyrest
        </li>
        <li class="price-chart__legend-item">
          <span class="price-chart__legend-dash" aria-hidden="true" />
          Gennemsnit
        </li>
      </ul>
      <div v-if="positionedPoints.length === 0" class="price-chart__empty">
        Ingen prisdata tilgængelige.
      </div>
      <svg
        v-else
        class="price-chart__svg"
        :viewBox="`0 0 ${VIEW_W} ${VIEW_H}`"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Linjegraf der viser spotpriser pr. time over døgnet"
        @pointermove="onPointerMove"
        @pointerleave="onPointerLeave"
      >
        <defs>
          <linearGradient id="price-chart-area-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" class="price-chart__area-stop-top" />
            <stop offset="100%" class="price-chart__area-stop-bottom" />
          </linearGradient>
          <linearGradient id="price-chart-optimal-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" class="price-chart__optimal-stop-top" />
            <stop offset="100%" class="price-chart__optimal-stop-bottom" />
          </linearGradient>
        </defs>

        <!-- Y axis grid + labels -->
        <g class="price-chart__grid">
          <line
            v-for="tick in yTicks"
            :key="`yg-${tick.value}`"
            :x1="PAD_LEFT"
            :x2="VIEW_W - PAD_RIGHT"
            :y1="tick.y"
            :y2="tick.y"
          />
        </g>
        <g class="price-chart__y-labels">
          <text
            v-for="tick in yTicks"
            :key="`yl-${tick.value}`"
            :x="PAD_LEFT - 8"
            :y="tick.y + 4"
            text-anchor="end"
          >
            {{ tick.label }}
          </text>
        </g>

        <!-- Average price line -->
        <line
          v-if="averageY !== null"
          class="price-chart__average-line"
          :x1="PAD_LEFT"
          :x2="VIEW_W - PAD_RIGHT"
          :y1="averageY"
          :y2="averageY"
        />

        <!-- Tomorrow divider -->
        <line
          v-if="todayDividerX !== null"
          class="price-chart__divider"
          :x1="todayDividerX"
          :x2="todayDividerX"
          :y1="PAD_TOP"
          :y2="VIEW_H - PAD_BOTTOM"
        />
        <text
          v-if="todayDividerX !== null"
          class="price-chart__divider-label"
          :x="todayDividerX + 6"
          :y="PAD_TOP + 12"
        >
          I morgen
        </text>

        <!-- Current hour marker -->
        <g v-if="currentHourX !== null" class="price-chart__now">
          <line
            class="price-chart__now-line"
            :x1="currentHourX"
            :x2="currentHourX"
            :y1="PAD_TOP"
            :y2="VIEW_H - PAD_BOTTOM"
          />
          <rect
            class="price-chart__now-pill-bg"
            :x="currentHourX - 18"
            :y="PAD_TOP - 22"
            width="36"
            height="18"
            rx="9"
          />
          <text
            class="price-chart__now-pill-text"
            :x="currentHourX"
            :y="PAD_TOP - 9"
            text-anchor="middle"
          >
            Nu
          </text>
        </g>

        <!-- Area fill under the line -->
        <path
          :key="`area-${animationKey}`"
          class="price-chart__area"
          :d="areaPath"
          fill="url(#price-chart-area-gradient)"
        />

        <!-- Green highlight bands for algorithm-picked charging hours -->
        <g class="price-chart__optimal-bands" aria-hidden="true">
          <path
            v-for="(d, i) in optimalBandPaths"
            :key="`optimal-${animationKey}-${i}`"
            class="price-chart__optimal-band"
            :d="d"
            fill="url(#price-chart-optimal-gradient)"
          />
        </g>

        <!-- Line -->
        <path :key="`line-${animationKey}`" class="price-chart__line" :d="linePath" />

        <!-- Markers -->
        <circle
          v-if="cheapestPoint"
          :key="`cheapest-${animationKey}`"
          class="price-chart__marker price-chart__marker--cheapest"
          :cx="cheapestPoint.x"
          :cy="cheapestPoint.y"
          r="6"
        />
        <circle
          v-if="expensivePoint"
          :key="`expensive-${animationKey}`"
          class="price-chart__marker price-chart__marker--expensive"
          :cx="expensivePoint.x"
          :cy="expensivePoint.y"
          r="6"
        />

        <!-- X axis labels -->
        <g class="price-chart__x-labels">
          <text
            v-for="tick in xTicks"
            :key="`xl-${tick.index}`"
            :x="tick.x"
            :y="VIEW_H - PAD_BOTTOM + 18"
            text-anchor="middle"
          >
            {{ formatHour(tick.hour) }}
          </text>
        </g>

        <!-- Tooltip -->
        <g v-if="tooltipPoint" class="price-chart__tooltip">
          <line
            :x1="tooltipPoint.x"
            :x2="tooltipPoint.x"
            :y1="PAD_TOP"
            :y2="VIEW_H - PAD_BOTTOM"
            class="price-chart__tooltip-line"
          />
          <circle
            :cx="tooltipPoint.x"
            :cy="tooltipPoint.y"
            r="5"
            class="price-chart__tooltip-dot"
          />
          <g
            :transform="`translate(${Math.min(
              Math.max(tooltipPoint.x - 60, PAD_LEFT),
              VIEW_W - PAD_RIGHT - 120,
            )}, ${PAD_TOP})`"
          >
            <rect width="120" height="44" rx="6" class="price-chart__tooltip-bg" />
            <text x="60" y="18" text-anchor="middle" class="price-chart__tooltip-text">
              {{ tooltipPoint.isTomorrow ? 'I morgen ' : '' }}{{ formatHour(tooltipPoint.hour) }}
            </text>
            <text
              x="60"
              y="34"
              text-anchor="middle"
              class="price-chart__tooltip-text price-chart__tooltip-text--bold"
            >
              {{ formatPricePerKwh(tooltipPoint.price) }}
            </text>
          </g>
        </g>
      </svg>

      <p class="price-chart__footnote">
        Priser vises som rå spotpriser ekskl. tariffer, afgifter og abonnement.
      </p>

      <PriceChartA11yTable :today="today" :tomorrow="tomorrow" />
    </div>
  </Accordion>
</template>

<style lang="scss" scoped>
.price-chart {
  background: color('white');
  border: 1px solid color('background-medium');
  border-radius: $radius-lg;

  :deep(.accordion__summary) {
    padding: space(5);
    color: color('primary');
  }

  :deep(.accordion__summary:hover) {
    color: color('accent-dark');
  }

  &__caption {
    font-size: $font-size-md;
    font-weight: $font-weight-semibold;
    color: color('primary');
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: space(2);
    padding: space(4) space(5) space(5);
    border-top: 1px solid color('background-medium');
  }

  &__svg {
    width: 100%;
    height: auto;
    max-height: 320px;
    touch-action: pan-y;
    font-family: $font-family-base;
    font-size: 11px;
  }

  &__empty {
    padding: space(8);
    text-align: center;
    color: color('ink-light');
  }

  &__grid line {
    stroke: color('background-light');
    stroke-width: 1;
    stroke-dasharray: 2 4;
  }

  &__y-labels text,
  &__x-labels text {
    fill: color('ink-light');
    font-size: 11px;
  }

  &__area {
    pointer-events: none;
    clip-path: inset(0 100% 0 0);
    animation: price-chart-reveal 2600ms $easing-standard forwards;
  }

  &__area-stop-top {
    stop-color: color('primary');
    stop-opacity: 0.22;
  }

  &__area-stop-bottom {
    stop-color: color('primary');
    stop-opacity: 0;
  }

  &__optimal-band {
    pointer-events: none;
    clip-path: inset(0 100% 0 0);
    animation: price-chart-reveal 2600ms $easing-standard forwards;
  }

  &__optimal-stop-top {
    stop-color: color('accent');
    stop-opacity: 0.3;
  }

  &__optimal-stop-bottom {
    stop-color: color('accent');
    stop-opacity: 0;
  }

  &__line {
    fill: none;
    stroke: color('primary');
    stroke-width: 2;
    stroke-linejoin: round;
    stroke-linecap: round;
    clip-path: inset(0 100% 0 0);
    animation: price-chart-reveal 2600ms $easing-standard forwards;
  }

  &__marker {
    stroke: color('white');
    stroke-width: 2;
    opacity: 0;
    animation: price-chart-marker-in 500ms $easing-standard 2500ms forwards;

    &--cheapest {
      fill: color('accent');
    }

    &--expensive {
      fill: color('warning');
    }
  }

  &__now-line {
    stroke: color('primary');
    stroke-width: 1.5;
    opacity: 0.7;
  }

  &__now-pill-bg {
    fill: color('primary');
  }

  &__now-pill-text {
    fill: color('white');
    font-size: 11px;
    font-weight: $font-weight-semibold;
  }

  &__average-line {
    stroke: color('primary-light');
    stroke-width: 1.5;
    stroke-dasharray: 3 4;
    opacity: 0.9;
  }

  &__divider {
    stroke: color('background-medium');
    stroke-width: 1;
    stroke-dasharray: 1 3;
  }

  &__divider-label {
    fill: color('ink-light');
    font-size: 10px;
    font-style: italic;
  }

  &__tooltip-line {
    stroke: color('primary');
    stroke-width: 1;
  }

  &__tooltip-dot {
    fill: color('primary');
    stroke: color('white');
    stroke-width: 2;
  }

  &__tooltip-bg {
    fill: color('primary');
    opacity: 0.95;
  }

  &__tooltip-text {
    fill: color('white');
    font-size: 11px;

    &--bold {
      font-weight: $font-weight-bold;
      font-size: 12px;
    }
  }

  &__legend {
    display: flex;
    justify-content: flex-end;
    gap: space(4);
    margin: 0 0 space(2);
    padding: 0;
    list-style: none;
  }

  &__legend-item {
    display: inline-flex;
    align-items: center;
    gap: space(2);
    font-size: $font-size-xs;
    color: color('ink-light');
  }

  &__legend-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;

    &--cheapest {
      background: color('accent');
    }

    &--expensive {
      background: color('warning');
    }
  }

  &__legend-dash {
    display: inline-block;
    width: 18px;
    height: 0;
    border-top: 2px dashed color('primary-light');
  }

  &__legend-band {
    display: inline-block;
    width: 14px;
    height: 10px;
    background: linear-gradient(180deg, rgb(0, 176, 62, 0.35) 0%, rgb(0, 176, 62, 0.05) 100%);
    border-radius: 2px;
  }

  &__footnote {
    margin: space(2) 0 0;
    font-size: $font-size-xs;
    color: color('ink-light');
    font-style: italic;
  }
}

@keyframes price-chart-reveal {
  from {
    clip-path: inset(0 100% 0 0);
  }

  to {
    clip-path: inset(0 0 0 0);
  }
}

@keyframes price-chart-marker-in {
  to {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .price-chart__line,
  .price-chart__area,
  .price-chart__optimal-band,
  .price-chart__marker {
    animation: none;
    clip-path: none;
    opacity: 1;
  }
}
</style>
